import React from 'react'
import { useIntl } from 'react-intl'
import { css } from '@emotion/css'
import { IconIp } from '@contrast-security-inc/design-system-foundations'
import { Text } from '@contrast-security-inc/design-system-typography'
import { PolicyManagementFormHeader } from '../legacy-policy-panel/policy-management-form-header'
import { updateRoute } from '../route'
import { SourceName } from '.'
import {
  LegacyPolicyPanel,
  LegacyPolicyPanelBody
} from '../legacy-policy-panel'
import { logError, logSuccess } from '../toaster'
import { useSourceNames } from './api/use-source-names'
import { SourceNameForm } from './source-name-form'
import {
  SourceNameFormProvider,
  useSourceNameFormContext
} from './source-name-form-context'
import { useCreateSourceName } from './api/use-create-source-name'
import { buildSourceNameVariables } from './shared'
import { FormValidator } from '../../legacy-design-system/form'
import { Button } from '@contrast-security-inc/design-system-buttons'

export interface Props {
  organizationID: string
}

const routeToSourceNameGrid = (organizationID: string) =>
  updateRoute(`/${organizationID}/rules/ip/source-names`)

export const AddSourceNameForm: React.FC<Props> = ({ organizationID }) => {
  const { isLoading, data: sourceNames, error } = useSourceNames(organizationID)

  if (error) {
    throw new Error(error.message)
  }

  return isLoading ? (
    <></>
  ) : (
    <SourceNameFormProvider
      sourceName={{
        id: '',
        name: '',
        cidrBlocks: [''],
        start: undefined,
        expiration: undefined
      }}
    >
      <AddSourceNameFormContent
        organizationID={organizationID}
        sourceNames={sourceNames as SourceName[]}
      />
    </SourceNameFormProvider>
  )
}

export const AddSourceNameFormContent = ({
  organizationID,
  sourceNames
}: {
  organizationID: string
  sourceNames: SourceName[]
}) => {
  const { formatMessage } = useIntl()
  const { state } = useSourceNameFormContext()
  const { mutate: addSourceName } = useCreateSourceName({
    onSuccess: () => {
      logSuccess(formatMessage({ id: 'detail_sourceNames_saveSuccessMessage' }))
      routeToSourceNameGrid(organizationID)
    },
    onError: (_error) => {
      logError(formatMessage({ id: 'detail_sourceNames_saveErrorMessage' }))
    }
  })

  return (
    <FormValidator value={state}>
      {({ getFormFieldProps, formIsValid, setCustomValidity }) => {
        return (
          <LegacyPolicyPanel
            outerFooter={
              <>
                <Button
                  data-e2e="add-source-name-button"
                  className={css`
                    margin-right: 20px;
                  `}
                  onClick={() => {
                    addSourceName({
                      organizationUuid: organizationID,
                      ...buildSourceNameVariables(state)
                    })
                  }}
                  isDisabled={!formIsValid}
                >
                  <Text font="body" color="white">
                    {formatMessage({ id: 'action_add' })}
                  </Text>
                </Button>
                <Button
                  variant="secondary"
                  className={css`
                    margin-right: 0.75em;
                  `}
                  onClick={() => routeToSourceNameGrid(organizationID)}
                >
                  <Text font="body" color="neutral100">
                    {formatMessage({ id: 'action_cancel' })}
                  </Text>
                </Button>
              </>
            }
          >
            <LegacyPolicyPanelBody>
              <PolicyManagementFormHeader
                i18nID="action_ipsManagement_addSourceName"
                Icon={IconIp}
                onClose={() => routeToSourceNameGrid(organizationID)}
              />
              <SourceNameForm
                setCustomValidity={setCustomValidity}
                organizationID={organizationID}
                getFormFieldProps={getFormFieldProps}
                existingSourceNames={sourceNames}
              />
            </LegacyPolicyPanelBody>
          </LegacyPolicyPanel>
        )
      }}
    </FormValidator>
  )
}
